import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Howler } from "howler";
import { type MusicSnapshot } from "@/lib/audio/musicConductor";
import { AudioEngine, getAudioEngine, resetAudioEngine } from "@/lib/audio/audioEngine";
import { type MusicContext } from "@/lib/audio/audioConfig";
import { haptics } from "@/lib/stores/useHaptics";
import { setAudioCacheBusterConfig } from "@/lib/audio/audioCacheBuster";

export type CameraPerspective = "top" | "tilted" | "firstPerson";

// Flag global para saber se audio foi desbloqueado
let audioUnlocked = false;

interface AudioState {
  // Legacy HTMLAudioElement refs (mantidos para compatibilidade durante migracao)
  backgroundMusic: HTMLAudioElement | null;
  hitSound: HTMLAudioElement | null;
  successSound: HTMLAudioElement | null;
  shootSound: HTMLAudioElement | null;
  explosionSound: HTMLAudioElement | null;
  chargeLoopSound: HTMLAudioElement | null;

  // Novo AudioEngine
  audioEngine: AudioEngine | null;
  musicSnapshot: MusicSnapshot | null;

  // Bloqueio de música (impede MusicAdapter de sobrescrever)
  musicLock: MusicContext | null;

  // Configuracoes persistidas
  isMuted: boolean;
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  audioCacheBusterEnabled: boolean;
  audioCacheBusterVersion: string;
  voiceNarrationEnabled: boolean; // Narração por voz (Eleven Labs) - desativado por padrão
  voiceNarrationVolume: number;
  targetFps: number;
  cameraPerspective: CameraPerspective;

  setTargetFps: (fps: number) => void;
  setCameraPerspective: (perspective: CameraPerspective) => void;
  setMusicLock: (context: MusicContext | null) => void;
  setVoiceNarrationEnabled: (enabled: boolean) => void;
  setVoiceNarrationVolume: (volume: number) => void;
  setAudioCacheBuster: (enabled: boolean, version?: string) => void;

  // Legacy setters (mantidos para compatibilidade)
  setBackgroundMusic: (music: HTMLAudioElement) => void;
  setHitSound: (sound: HTMLAudioElement) => void;
  setSuccessSound: (sound: HTMLAudioElement) => void;
  setShootSound: (sound: HTMLAudioElement) => void;
  setExplosionSound: (sound: HTMLAudioElement) => void;

  toggleMute: () => void;
  setMasterVolume: (volume: number) => void;
  setMusicVolume: (volume: number) => void;
  setSfxVolume: (volume: number) => void;

  playHit: () => void;
  playSuccess: () => void;
  playShoot: () => void;
  playExplosion: () => void;
  playChargedShoot: (chargeLevel: number) => void;
  playUiNavigate: () => void;
  /**
   * Som de uma virada de terreno (mergulhou, acendeu, apagou, atolou).
   *
   * `intensity` abaixa o volume do que aconteceu com os outros tanques: é
   * informação periférica, não o que está acontecendo com você.
   */
  playTerrainSfx: (sfxId: string, intensity?: number) => void;

  // Musica — agora roteado pelo AudioEngine
  startMusic: (context?: MusicContext, force?: boolean) => void;
  stopMusic: () => void;
  ensureAudioEngine: () => AudioEngine;
  updateAdaptiveMusic: (snapshot: MusicSnapshot) => void;
  unlockAudio: () => void;

  // Legacy compat aliases
  ensureMusicConductor: () => AudioEngine;

  // Voice ducking — abaixa música durante narração de voz
  duckMusicForVoice: () => void;
  unduckMusicForVoice: () => void;

  startChargeSound: () => void;
  updateChargeSound: (chargeLevel: number) => void;
  stopChargeSound: (chargeLevel: number) => void;
}

export const useAudio = create<AudioState>()(
  persist(
    (set, get) => ({
      backgroundMusic: null,
      hitSound: null,
      successSound: null,
      shootSound: null,
      explosionSound: null,
      chargeLoopSound: null,
      audioEngine: null,
      musicSnapshot: null,
      musicLock: null,
      isMuted: false,
      masterVolume: 0.7,
      musicVolume: 0.5,
      sfxVolume: 0.8,
      audioCacheBusterEnabled: false,
      audioCacheBusterVersion: "",
      voiceNarrationEnabled: false, // Desativado por padrão
      voiceNarrationVolume: 1.0,
      targetFps: 60,
      cameraPerspective: "top" as CameraPerspective,

      setTargetFps: (fps) => set({ targetFps: fps }),
      setCameraPerspective: (perspective) => set({ cameraPerspective: perspective }),
      setMusicLock: (context) => set({ musicLock: context }),
      setVoiceNarrationEnabled: (enabled) => set({ voiceNarrationEnabled: enabled }),
      setVoiceNarrationVolume: (volume) => set({ voiceNarrationVolume: volume }),
      setAudioCacheBuster: (enabled, version) => {
        const safeVersion = version ?? "";
        set({ audioCacheBusterEnabled: enabled, audioCacheBusterVersion: safeVersion });
        setAudioCacheBusterConfig(enabled, safeVersion);
        if (get().audioEngine) {
          resetAudioEngine();
          set({ audioEngine: null });
        }
      },

      // Legacy setters
      setBackgroundMusic: (music) => set({ backgroundMusic: music }),
      setHitSound: (sound) => set({ hitSound: sound }),
      setSuccessSound: (sound) => set({ successSound: sound }),
      setShootSound: (sound) => set({ shootSound: sound }),
      setExplosionSound: (sound) => set({ explosionSound: sound }),

      ensureAudioEngine: () => {
        let engine = get().audioEngine;
        if (!engine && typeof window !== "undefined") {
          engine = getAudioEngine();
          engine.setVolumes(get().masterVolume, get().musicVolume, get().sfxVolume);
          engine.setMuted(get().isMuted);
          set({ audioEngine: engine });
        }
        return engine!;
      },

      // Alias para compatibilidade com MusicAdapter
      ensureMusicConductor: () => get().ensureAudioEngine(),

      toggleMute: () => {
        const { isMuted } = get();
        const newMutedState = !isMuted;
        set({ isMuted: newMutedState });

        const engine = get().audioEngine;
        if (engine) engine.setMuted(newMutedState);
      },

      setMasterVolume: (volume) => {
        set({ masterVolume: volume });
        const engine = get().audioEngine;
        if (engine) engine.setVolumes(volume, get().musicVolume, get().sfxVolume);
      },

      setMusicVolume: (volume) => {
        set({ musicVolume: volume });
        const engine = get().audioEngine;
        if (engine) engine.setVolumes(get().masterVolume, volume, get().sfxVolume);
      },

      setSfxVolume: (volume) => {
        set({ sfxVolume: volume });
        const engine = get().audioEngine;
        if (engine) engine.setVolumes(get().masterVolume, get().musicVolume, volume);
      },

      updateAdaptiveMusic: (snapshot) => {
        set({ musicSnapshot: snapshot });
        const engine = get().ensureAudioEngine();
        engine.updateFromSnapshot(snapshot);
      },

      playHit: () => {
        const engine = get().ensureAudioEngine();
        engine.playSfx('hit');
        haptics.impact();
      },

      playSuccess: () => {
        const engine = get().ensureAudioEngine();
        engine.playSfx('success');
        haptics.collect();
      },

      playShoot: () => {
        const engine = get().ensureAudioEngine();
        engine.playSfx('shoot');
        haptics.shoot();
      },

      playExplosion: () => {
        const engine = get().ensureAudioEngine();
        engine.playSfx('explosion');
        haptics.explosion();
      },

      playUiNavigate: () => {
        const engine = get().ensureAudioEngine();
        engine.playSfx('ui_nav');
      },

      playTerrainSfx: (sfxId, intensity = 1) => {
        if (intensity <= 0) return;
        const engine = get().ensureAudioEngine();
        // O volume-base de cada som vive no catálogo de SFX; aqui só entra a
        // atenuação de "não foi comigo".
        if (intensity >= 1) engine.playSfx(sfxId);
        else engine.playSfxScaled(sfxId, intensity);
      },

      startMusic: (context?: MusicContext, force?: boolean) => {
        const { musicLock } = get();
        const targetContext = context ?? 'battle';
        
        // Se há um lock ativo e não é forçado, só permite tocar o contexto bloqueado
        if (musicLock && !force && targetContext !== musicLock) {
          console.log(`[Audio] Music locked to '${musicLock}', ignoring request for '${targetContext}'`);
          return;
        }
        
        get().unlockAudio();
        const engine = get().ensureAudioEngine();
        
        // Verificar se AudioContext está pronto e tocar música
        // O Howler pode ter um delay na inicialização
        const playWhenReady = () => {
          if (Howler.ctx && Howler.ctx.state === 'running') {
            engine.playMusic(targetContext);
          } else if (Howler.ctx && Howler.ctx.state === 'suspended') {
            Howler.ctx.resume().then(() => {
              engine.playMusic(targetContext);
            }).catch(() => {});
          } else {
            // Fallback: tentar tocar diretamente (pode funcionar em alguns navegadores)
            engine.playMusic(targetContext);
          }
        };
        
        // Pequeno delay para garantir que o AudioContext teve tempo de inicializar
        setTimeout(playWhenReady, 50);
      },

      stopMusic: () => {
        const engine = get().audioEngine;
        if (engine) {
          engine.stopMusic();
        }
      },

      duckMusicForVoice: () => {
        const engine = get().audioEngine;
        if (engine) {
          engine.duckMusicForVoice();
        }
      },

      unduckMusicForVoice: () => {
        const engine = get().audioEngine;
        if (engine) {
          engine.unduckMusicForVoice();
        }
      },

      playChargedShoot: (chargeLevel: number) => {
        const engine = get().ensureAudioEngine();
        engine.playSfx('charged_shoot', chargeLevel);
      },

      startChargeSound: () => {
        const { hitSound, isMuted, masterVolume, sfxVolume } = get();
        if (isMuted || !hitSound) return;

        const loopSound = hitSound.cloneNode() as HTMLAudioElement;
        loopSound.volume = masterVolume * sfxVolume * 0.1;
        loopSound.loop = true;
        loopSound.playbackRate = 0.5;
        loopSound.play().catch(() => {});
        set({ chargeLoopSound: loopSound });
      },

      updateChargeSound: (chargeLevel: number) => {
        const { chargeLoopSound, masterVolume, sfxVolume, isMuted } = get();
        if (!chargeLoopSound || isMuted) return;

        const safeLevel = Math.max(0, Math.min(1, chargeLevel || 0));
        const volume = Math.max(0, Math.min(1, masterVolume * sfxVolume * (0.1 + safeLevel * 0.2)));
        const rate = Math.max(0.1, Math.min(4, 0.5 + safeLevel * 1.5));

        if (Number.isFinite(volume)) chargeLoopSound.volume = volume;
        if (Number.isFinite(rate)) chargeLoopSound.playbackRate = rate;
      },

      stopChargeSound: (chargeLevel: number) => {
        const { chargeLoopSound } = get();

        if (chargeLoopSound) {
          chargeLoopSound.pause();
          chargeLoopSound.currentTime = 0;
          set({ chargeLoopSound: null });
        }
      },

      unlockAudio: () => {
        if (audioUnlocked) return;
        audioUnlocked = true;

        const engine = get().ensureAudioEngine();
        engine.forceResume();

        console.log("[Audio] Audio unlocked via user interaction");
      },
    }),
    {
      name: "rocketanks-audio",
      partialize: (state) => ({
        isMuted: state.isMuted,
        masterVolume: state.masterVolume,
        musicVolume: state.musicVolume,
        sfxVolume: state.sfxVolume,
        targetFps: state.targetFps,
        cameraPerspective: state.cameraPerspective,
      }),
    }
  )
);
